import { SortOrder } from '@/api/generatedApi';
import { DependencyFilter, DependencySortField } from '@/api/dependencyTypes';
import { Box, FormControl, IconButton, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { createUseStyles } from '@/services/createUseStyles';

const useStyles = createUseStyles({
  DependencyTreeToolbar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '4px 8px 6px',
    fontSize: '0.75rem',
  },
  DependencyTreeToolbar_row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  DependencyTreeToolbar_label: {
    color: 'text.secondary',
    fontSize: '0.75rem',
    minWidth: '48px',
  },
  DependencyTreeToolbar_select: {
    flex: 1,
    fontSize: '0.75rem',
    '& .MuiSelect-select': {
      paddingTop: '2px',
      paddingBottom: '2px',
    },
  },
});

const FILTER_OPTIONS: { value: DependencyFilter; label: string }[] = [
  { value: DependencyFilter.All, label: 'All dependencies' },
  { value: DependencyFilter.Missing, label: 'Missing only' },
  { value: DependencyFilter.Available, label: 'Available only' },
];

const SORT_OPTIONS: { value: DependencySortField; label: string }[] = [
  { value: DependencySortField.Status, label: 'Status' },
  { value: DependencySortField.Name, label: 'Name' },
  { value: DependencySortField.Path, label: 'Path' },
  { value: DependencySortField.Occurrences, label: 'Occurrences' },
];

export interface DependencyTreeToolbarProperties {
  filter: DependencyFilter;
  sortBy: DependencySortField;
  sortOrder: SortOrder;
  onFilterChange: (filter: DependencyFilter) => void;
  onSortByChange: (sortBy: DependencySortField) => void;
  onSortOrderChange: (sortOrder: SortOrder) => void;
}

export const DependencyTreeToolbar = ({
  filter,
  sortBy,
  sortOrder,
  onFilterChange,
  onSortByChange,
  onSortOrderChange,
}: DependencyTreeToolbarProperties) => {
  const c = useStyles();

  const handleFilter = (event: SelectChangeEvent<DependencyFilter>) =>
    onFilterChange(event.target.value as DependencyFilter);
  const handleSortBy = (event: SelectChangeEvent<DependencySortField>) =>
    onSortByChange(event.target.value as DependencySortField);
  const toggleOrder = () => onSortOrderChange(sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC);

  return (
    <Box className={c.DependencyTreeToolbar} data-testid='dependency-tree-toolbar'>
      <Box className={c.DependencyTreeToolbar_row}>
        <Box component='span' className={c.DependencyTreeToolbar_label}>
          Show:
        </Box>
        <FormControl size='small' variant='standard' sx={{ flex: 1 }}>
          <Select<DependencyFilter>
            value={filter}
            onChange={handleFilter}
            className={c.DependencyTreeToolbar_select}
            data-testid='dependency-filter-select'
            inputProps={{ 'aria-label': 'Filter dependencies' }}
          >
            {FILTER_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value} data-testid={`dependency-filter-${option.value}`}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box className={c.DependencyTreeToolbar_row}>
        <Box component='span' className={c.DependencyTreeToolbar_label}>
          Sort by:
        </Box>
        <FormControl size='small' variant='standard' sx={{ flex: 1 }}>
          <Select<DependencySortField>
            value={sortBy}
            onChange={handleSortBy}
            className={c.DependencyTreeToolbar_select}
            data-testid='dependency-sort-select'
            inputProps={{ 'aria-label': 'Sort dependencies' }}
          >
            {SORT_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value} data-testid={`dependency-sort-${option.value}`}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title={sortOrder === SortOrder.ASC ? 'Ascending' : 'Descending'}>
          <IconButton
            size='small'
            onClick={toggleOrder}
            data-testid='dependency-sort-order-toggle'
            aria-label={`Toggle sort direction (currently ${sortOrder === SortOrder.ASC ? 'ascending' : 'descending'})`}
          >
            {sortOrder === SortOrder.ASC ? <ArrowUpward fontSize='small' /> : <ArrowDownward fontSize='small' />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
